import {
  ToggleWmsLayerInSettings,
  useWMSSettings,
} from '../../../WMSLayers/useWMSSettings';
import React, { useState } from 'react';
import {
  LayerItem,
  useLayers,
  useWmsToTileLayer,
} from '@ais-ui/react-openlayers';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { PiGlobeHemisphereEastThin } from 'react-icons/pi';
import { useSaveSettings, WMSInfo, WMSLayerInfo } from '@ais-ui/websockets';
import { useSettings } from '../../../Settings/SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { CheckIcon } from '@radix-ui/react-icons';
import { EmptyCommand } from '../../../../components/EmptyCommand';
import { ROUTES } from '../../../../routes';

export const useRemoveWmsLayer = () => {
  const { messages } = useLocalization();
  const { settings } = useSettings();
  const { toast } = useToast();
  const wmsSettings = useWMSSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  const removeWmsLayer = (layerItem: LayerItem) => {
    const settingsToSave = ToggleWmsLayerInSettings(
      wmsSettings.wmsInfos,
      layerItem.id,
      layerItem.name,
      false,
      settings
    );

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'enable-wms-layer-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Removed WMS layer, ${layerItem.name}, due to load failure`,
        });
      },
      onError: () => {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: `Failed to remove WMS layer, ${layerItem.name}, due to load failure`,
          variant: 'destructive',
        });
      },
    });
  };

  return removeWmsLayer;
};

export const WMSLayers = () => {
  const { messages } = useLocalization();
  const wmsSettings = useWMSSettings();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const { addExistingLayerItem, removeLayer, getLayerById, doesLayerExist } =
    useLayers();
  const removeWmsLayer = useRemoveWmsLayer();
  const onWmsToTileLayerWithLayerItem = useWmsToTileLayer(removeWmsLayer);

  const onWMSSelect = async (wmsInfo: WMSInfo, wmsLayerInfo: WMSLayerInfo) => {
    const enable = !doesLayerExist(wmsLayerInfo.id);

    if (enable) {
      try {
        const { tileLayer, layerItem } = await onWmsToTileLayerWithLayerItem(
          wmsInfo,
          wmsLayerInfo
        );

        if (!tileLayer || !layerItem) {
          return;
        }

        addExistingLayerItem(tileLayer, layerItem);
      } catch (error) {
        toast({
          className: 'enable-wms-layer-error-toast',
          title: messages.genericToastTitleError,
          description: messages.mapMenuWMSFailedToEnableLayer.replace(
            '{label}',
            wmsLayerInfo.title
          ),
          variant: 'destructive',
        });
      }
    } else {
      const layerItem = getLayerById(wmsLayerInfo.id);
      if (!layerItem) {
        return;
      }
      removeLayer(layerItem.layer);
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="add-wms-layer-actions-button"
              size="icon"
              variant="outline"
              className="h-10 w-10 flex-shrink-0 flex-grow-0"
            >
              <PiGlobeHemisphereEastThin className="h-6 w-6"></PiGlobeHemisphereEastThin>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Add WMS layers</TooltipContent>
      </Tooltip>
      <PopoverContent>
        {!wmsSettings ||
        !wmsSettings.wmsInfos ||
        wmsSettings.wmsInfos.length === 0 ? (
          <EmptyCommand
            entity="web map service"
            srcImage="empty_list_bro.png"
            to={ROUTES.SETTINGS.WEB_MAP_SERVICES.path}
          ></EmptyCommand>
        ) : (
          <Command>
            <CommandInput placeholder="Find WMS layer..." />

            <CommandList className="overflow-hidden h-60">
              <ScrollArea className="flex-grow overflow-y-auto pr-4 h-60">
                {wmsSettings.wmsInfos.map((wmsLayer, outerIndex) => (
                  <CommandGroup key={wmsLayer.id} heading={wmsLayer.name}>
                    {wmsLayer?.layers?.sort((a,b) => a.title.localeCompare(b.title)).map((layer, innerIndex) => (
                      <CommandItem
                        key={layer.id}
                        value={`${layer.title}__${outerIndex}__${innerIndex}`}
                        onSelect={() => onWMSSelect(wmsLayer, layer)}
                      >
                        <div className="flex justify-between w-full">
                          <div>{layer.title}</div>
                          {doesLayerExist(layer.id) && (
                            <CheckIcon className="h-4 w-4" />
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </ScrollArea>
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
};
